import type { UseQueryResult } from '@tanstack/react-query';
import { CloudOff, RotateCw, type LucideIcon } from 'lucide-react';

import { ApiError, errorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { LoadingAnnouncer } from './ui/skeleton';

/**
 * The states every screen has, drawn the same way each time.
 *
 * An empty state OWNS the region it is in. A small centred block pinned under
 * the header with a screenful of nothing below it reads as a page that failed
 * to load, not as a considered zero-state — so these fill their container and
 * say what will be here and what to do first.
 */

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  aside,
  className,
}: {
  icon: LucideIcon;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** A preview of what this will look like once it is not empty. */
  aside?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex min-h-[22rem] flex-1 flex-col items-center justify-center gap-5 rounded-2xl',
        'border border-dashed border-line-strong bg-sunken/60 px-5 py-12 text-center',
        className,
      )}
    >
      <span className="grid size-14 place-items-center rounded-2xl bg-muted text-ink-muted">
        <Icon className="size-7" aria-hidden="true" />
      </span>

      <div className="grid max-w-[34ch] gap-2">
        <h2 className="text-xl font-bold tracking-tight text-ink">{title}</h2>
        {description ? (
          <p className="text-[0.9375rem] leading-normal text-ink-muted">{description}</p>
        ) : null}
      </div>

      {action ? <div className="flex flex-wrap justify-center gap-3">{action}</div> : null}
      {aside ? <div className="w-full pt-2">{aside}</div> : null}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  compact = false,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
  className?: string;
}) {
  /*
   * The backend writes readable sentences — "That order is packing, it is not
   * ready to collect yet", "Incorrect code. 3 attempt(s) left." — so replacing
   * them with a generic apology throws away the only part the rider can act
   * on, standing where they are standing.
   */
  const message = errorMessage(error);
  const code = error instanceof ApiError ? error.code : null;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-4 rounded-2xl border border-error-line/50',
        'bg-error-surface text-center',
        compact ? 'px-5 py-8' : 'min-h-[18rem] px-5 py-12',
        className,
      )}
    >
      <CloudOff className={cn('text-error-ink', compact ? 'size-6' : 'size-8')} aria-hidden="true" />
      <p className="max-w-[40ch] text-[0.9375rem] leading-normal text-ink">{message}</p>
      {onRetry ? (
        <Button variant="secondary" size={compact ? 'sm' : 'md'} onClick={onRetry}>
          <RotateCw aria-hidden="true" />
          Try again
        </Button>
      ) : null}
      {code && code !== 'NETWORK' ? (
        <p className="text-xs uppercase tracking-caps text-ink-subtle">{code}</p>
      ) : null}
    </div>
  );
}

/**
 * Loading, error, and data — wired once rather than at every call site.
 *
 * `skeleton` is a prop rather than a default spinner because the right
 * placeholder is the shape of the thing being fetched, and only the caller
 * knows that shape.
 *
 * Only a FIRST load blocks. Once there is data, a refetch leaves it on screen:
 * a rider halfway through a delivery must not lose sight of the address to a
 * poll that happened to fail in a lift.
 */
export function QueryBoundary<T>({
  query,
  skeleton,
  children,
  compactError = false,
}: {
  query: UseQueryResult<T>;
  skeleton: React.ReactNode;
  children: (data: T) => React.ReactNode;
  compactError?: boolean;
}) {
  if (query.isPending) {
    return (
      <>
        <LoadingAnnouncer />
        {skeleton}
      </>
    );
  }

  if (query.isError && query.data === undefined) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} compact={compactError} />;
  }

  return <>{children(query.data as T)}</>;
}

/** The small-caps rule above a group of rows. */
export function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h2 className={cn('eyebrow pb-2 pt-6', className)}>{children}</h2>;
}
